import { create } from 'zustand';

import {
  ProjectOutlineStoreState,
  ProjectOutlineNode,
  ProjectOutlineNodeStatus,
  ProjectOutlineOnboardingStates,
} from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/types';
import { OnboardingStepType } from '@scale/shared/onboardingControlPanel';

export const useProjectOutlineStore = create<ProjectOutlineStoreState>(set => ({
  projectName: '',
  currentNodeIndex: 0,
  completedNodeIndex: -1,
  trainingNodes: [],
  assessmentNodes: [],
  taskingNodes: [],
  isVisible: false,
  taskingPayRate: undefined,
  assessmentPayRate: undefined,
  courseMap: {},
  setProjectName: projectName => set({ projectName }),
  setNodes: nodes =>
    set({
      trainingNodes: nodes.trainingNodes,
      assessmentNodes: nodes.assessmentNodes,
      taskingNodes: nodes.taskingNodes,
    }),
  setCourseMap: courseMap => set({ courseMap }),
  setCurrentNodeIndex: currentNodeIndex => set({ currentNodeIndex }),
  setCompletedNodeIndex: completedNodeIndex => set({ completedNodeIndex }),
  showProjectOutline: () =>
    set({
      isVisible: true,
    }),
  hideProjectOutline: () =>
    set({
      isVisible: false,
    }),
  toggleProjectOutline: () => set(({ isVisible }) => ({ isVisible: !isVisible })),
  setPayRates: payRates =>
    set({
      ...payRates,
    }),
}));

export const useOutlineNodes = () => {
  const trainingNodes = useProjectOutlineStore(state => state.trainingNodes);
  const assessmentNodes = useProjectOutlineStore(state => state.assessmentNodes);
  const taskingNodes = useProjectOutlineStore(state => state.taskingNodes);

  return [...trainingNodes, ...assessmentNodes, ...taskingNodes];
};

export const useGetOnboardingState = () => {
  const trainingNodes = useProjectOutlineStore(state => state.trainingNodes);
  const assessmentNodes = useProjectOutlineStore(state => state.assessmentNodes);
  const taskingNodes = useProjectOutlineStore(state => state.taskingNodes);

  const [trainingEndIndex, assessmentEndIndex, taskingEndIndex] = [
    trainingNodes.length,
    assessmentNodes.length,
    taskingNodes.length,
  ].reduce((acc, curr) => {
    if (acc.length === 0) {
      return acc.concat(curr - 1);
    }
    return acc.concat(acc[acc.length - 1] + curr);
  }, [] as number[]);

  const completedNodeIndex = useProjectOutlineStore(state => state.completedNodeIndex);

  if (completedNodeIndex <= trainingEndIndex) {
    return ProjectOutlineOnboardingStates.Training;
  } else if (assessmentEndIndex === trainingEndIndex && completedNodeIndex <= taskingEndIndex) {
    // If there are no assessment nodes, the indices will be the same
    return ProjectOutlineOnboardingStates.Tasking;
  } else if (completedNodeIndex <= assessmentEndIndex) {
    return ProjectOutlineOnboardingStates.Assessment;
  } else {
    return ProjectOutlineOnboardingStates.Tasking;
  }
};

export const useIsInTraining = () => {
  return useGetOnboardingState() === ProjectOutlineOnboardingStates.Training;
};

export const useGetOutlineNodeStatus = (node: ProjectOutlineNode) => {
  const currentNodeIndex = useProjectOutlineStore(state => state.currentNodeIndex);
  const completedNodeIndex = useProjectOutlineStore(state => state.completedNodeIndex);
  const outlineNodes = useOutlineNodes();

  let nodeIndex = -1;
  if ('courseId' in node) {
    nodeIndex = outlineNodes.findIndex(
      outlineNode =>
        outlineNode.type === OnboardingStepType.Course && outlineNode.courseId === node.courseId,
    );
  } else {
    nodeIndex = outlineNodes.findIndex(outlineNode => outlineNode.type === node.type);
  }

  const selected = currentNodeIndex === nodeIndex;

  if (nodeIndex < completedNodeIndex) {
    return {
      nodeIndex,
      selected,
      status: ProjectOutlineNodeStatus.COMPLETED,
      enabled: node.type === OnboardingStepType.Course,
    };
  } else if (nodeIndex === completedNodeIndex) {
    return {
      nodeIndex,
      selected,
      status: ProjectOutlineNodeStatus.IN_PROGRESS,
      enabled: true,
    };
  } else {
    return {
      nodeIndex,
      selected,
      status: ProjectOutlineNodeStatus.NOT_STARTED,
      enabled: false,
    };
  }
};

export const useGetOutlineSectionStatus = () => {
  const trainingNodes = useProjectOutlineStore(state => state.trainingNodes);
  const assessmentNodes = useProjectOutlineStore(state => state.assessmentNodes);
  const taskingNodes = useProjectOutlineStore(state => state.taskingNodes);

  const completedNodeIndex = useProjectOutlineStore(state => state.completedNodeIndex);

  const trainingEndIndex = trainingNodes.length;
  const assessmentEndIndex = trainingEndIndex + assessmentNodes.length;
  const taskingEndIndex = assessmentEndIndex + taskingNodes.length;

  if (completedNodeIndex <= trainingEndIndex) {
    return {
      trainingStatus:
        completedNodeIndex === trainingEndIndex
          ? ProjectOutlineNodeStatus.COMPLETED
          : ProjectOutlineNodeStatus.IN_PROGRESS,
      assessmentStatus: ProjectOutlineNodeStatus.NOT_STARTED,
      taskingStatus: ProjectOutlineNodeStatus.NOT_STARTED,
    };
  } else if (completedNodeIndex <= assessmentEndIndex) {
    return {
      trainingStatus: ProjectOutlineNodeStatus.COMPLETED,
      assessmentStatus:
        completedNodeIndex === assessmentEndIndex
          ? ProjectOutlineNodeStatus.COMPLETED
          : ProjectOutlineNodeStatus.IN_PROGRESS,
      taskingStatus: ProjectOutlineNodeStatus.NOT_STARTED,
    };
  } else {
    return {
      trainingStatus: ProjectOutlineNodeStatus.COMPLETED,
      assessmentStatus: ProjectOutlineNodeStatus.COMPLETED,
      taskingStatus:
        completedNodeIndex === taskingEndIndex
          ? ProjectOutlineNodeStatus.COMPLETED
          : ProjectOutlineNodeStatus.IN_PROGRESS,
    };
  }
};
